
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function Benefits() {
  const benefits = [
    {
      title: 'Ahorra horas de trabajo administrativo cada día',
      description: 'Automatiza la gestión de citas, historiales y comunicaciones con pacientes. Tu equipo se enfoca en lo que importa: la atención oftalmológica de calidad.',
      stat: '10+ horas',
      statLabel: 'ahorradas por semana',
      icon: 'ri-time-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20ophthalmology%20clinic%20reception%20area%20with%20staff%20using%20digital%20tablets%20and%20computers%20for%20patient%20management%20bright%20clean%20medical%20facility%20with%20emerald%20green%20accent%20colors%20minimalist%20professional%20atmosphere%20warm%20lighting&width=600&height=420&seq=benefit-opht-1&orientation=landscape',
    },
    {
      title: 'Incrementa tus ingresos sin aumentar tu equipo',
      description: 'Optimiza el flujo de pacientes, reduce las ausencias con recordatorios automáticos y mejora la retención con seguimiento personalizado de cada caso oftalmológico.',
      stat: '+25%',
      statLabel: 'más pacientes atendidos',
      icon: 'ri-line-chart-line',
      image: 'https://readdy.ai/api/search-image?query=ophthalmologist%20doctor%20reviewing%20patient%20growth%20analytics%20on%20modern%20computer%20screen%20showing%20upward%20trending%20charts%20and%20graphs%20in%20professional%20eye%20clinic%20office%20emerald%20green%20and%20white%20color%20scheme%20clean%20modern%20design&width=600&height=420&seq=benefit-opht-2&orientation=landscape',
    },
    {
      title: 'Diagnósticos más precisos con datos centralizados',
      description: 'Accede al historial completo del paciente en segundos. Compara evoluciones, analiza tendencias de presión intraocular y toma decisiones clínicas basadas en datos reales.',
      stat: '98%',
      statLabel: 'satisfacción de profesionales',
      icon: 'ri-search-eye-line',
      image: 'https://readdy.ai/api/search-image?query=close%20up%20of%20modern%20eye%20examination%20equipment%20with%20digital%20display%20showing%20retinal%20scan%20data%20in%20professional%20ophthalmology%20clinic%20emerald%20green%20LED%20accents%20dark%20sophisticated%20medical%20technology%20environment&width=600&height=420&seq=benefit-opht-3&orientation=landscape',
    },
  ];

  return (
    <section id="beneficios" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">Beneficios</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Resultados reales para tu <span className="font-display italic text-emerald-600">práctica</span>
            </h2>
          </div>
        </AnimatedSection>

        <div className="space-y-20">
          {benefits.map((benefit, index) => (
            <div key={index} className="grid lg:grid-cols-2 gap-12 items-center">
              <AnimatedSection animation={index % 2 === 0 ? 'slide-left' : 'slide-right'} className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="space-y-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <i className={`${benefit.icon} text-emerald-600 text-xl`}></i>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                  <div className="inline-flex items-center space-x-4 p-5 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{benefit.stat}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{benefit.statLabel}</p>
                    </div>
                  </div>
                  <div className="pt-2">
                    <a href="https://anamnesysoft.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-emerald-600 font-semibold text-sm hover:text-emerald-700 transition-colors cursor-pointer">
                      <span className="whitespace-nowrap">Conocer más</span>
                      <i className="ri-arrow-right-line ml-2 text-base"></i>
                    </a>
                  </div>
                </div>
              </AnimatedSection>
              <AnimatedSection animation={index % 2 === 0 ? 'slide-right' : 'slide-left'} className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100">
                  <div className="w-full h-80 lg:h-96">
                    <img
                      src={benefit.image}
                      alt={benefit.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </AnimatedSection>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
